"use client"

import { useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Edit, Building2, MapPin, Users, UserPlus, Phone, Globe, Instagram, Facebook, Linkedin, FileText } from "lucide-react"
import { Tabs, TabsContent } from "@/components/Tabs"
import { usePermissions } from "@/utils/auth/usePermissions"
import { useUser } from "@/utils/userContext/UserContext"
import api from "@/utils/connector/ApiConnector"
import CreateUserModal from "./components/CreateUserModal"
import EditCoworkingModal from "./components/EditCoworkingModal"
import UsersList from "./components/UsersList"
import "./MeuCoworking.css"

interface CoworkingData {
  id: string;
  name: string;
  instagram?: string;
  facebook?: string;
  linkedin?: string;
  whatsapp?: string;
  phone?: string;
  website?: string;
  address?: string;
  capacity?: number;
  cnpj?: string;
}

const MeuCoworking = () => {
  const [activeTab] = useState("users")
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const { canCreateUsers, isOwner } = usePermissions()
  const { userData } = useUser()
  const queryClient = useQueryClient()

  // React Query for coworking data
  const { data: coworkingData, isLoading: loading } = useQuery<CoworkingData | null>({
    queryKey: ['coworkingData'],
    queryFn: async () => {
      const response = await api.get("/coworking/my", {
        headers: {
          Authorization: `Bearer ${userData?.auth}`,
        },
      })
      return (response as any)?.data ?? response
    },
    enabled: !!userData?.auth,
  })

  const handleUserCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['coworkingUsers'] })
  }

  const handleCoworkingUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['coworkingData'] })
  }

  return (
    <>
      <div className="coworking-header">
        <div className="coworking-heading-wrap">
          <h1 className="gradient-heading">Minha Empresa</h1>
          <p className="dashboard-page-subtitle">Gerencie informações da empresa, equipe e estrutura.</p>
        </div>
        <div className="header-actions">
          {canCreateUsers() && (
            <button
              className="create-user-button"
              onClick={() => setIsCreateUserModalOpen(true)}
            >
              <UserPlus size={16} />
              Novo Usuário
            </button>
          )}
          {isOwner && (
            <button
              className="edit-button"
              onClick={() => setIsEditModalOpen(true)}
            >
              <Edit size={16} />
              Editar Informações
            </button>
          )}
        </div>
      </div>

      <div className="info-grid">
        <div className="info-card">
          <div className="card-header">
            <h3 className="card-title">Informações Gerais</h3>
            <p className="card-description">Detalhes do seu espaço de coworking</p>
          </div>
          <div className="card-content">
            {loading ? (
              <div className="loading-placeholder">Carregando...</div>
            ) : (
              <>
                <div className="info-item">
                  <div className="info-icon">
                    <Building2 size={20} />
                  </div>
                  <div className="info-content">
                    <h4 className="info-title">Nome do Espaço</h4>
                    <p className="info-value">{coworkingData?.name || "Não informado"}</p>
                  </div>
                </div>

                {coworkingData?.cnpj && (
                  <div className="info-item">
                    <div className="info-icon">
                      <FileText size={20} />
                    </div>
                    <div className="info-content">
                      <h4 className="info-title">CNPJ</h4>
                      <p className="info-value">{coworkingData.cnpj}</p>
                    </div>
                  </div>
                )}

                <div className="info-item">
                  <div className="info-icon">
                    <MapPin size={20} />
                  </div>
                  <div className="info-content">
                    <h4 className="info-title">Endereço</h4>
                    <p className="info-value">{coworkingData?.address || "Não informado"}</p>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">
                    <Users size={20} />
                  </div>
                  <div className="info-content">
                    <h4 className="info-title">Capacidade</h4>
                    <p className="info-value">
                      {coworkingData?.capacity ? `${coworkingData.capacity} pessoas` : "Não informado"}
                    </p>
                  </div>
                </div>

                {(coworkingData?.whatsapp || coworkingData?.phone) && (
                  <div className="info-item">
                    <div className="info-icon">
                      <Phone size={20} />
                    </div>
                    <div className="info-content">
                      <h4 className="info-title">Contato</h4>
                      <p className="info-value">
                        {coworkingData?.whatsapp && <span>WhatsApp: {coworkingData.whatsapp}</span>}
                        {coworkingData?.whatsapp && coworkingData?.phone && <br />}
                        {coworkingData?.phone && <span>Tel: {coworkingData.phone}</span>}
                      </p>
                    </div>
                  </div>
                )}

                {coworkingData?.website && (
                  <div className="info-item">
                    <div className="info-icon">
                      <Globe size={20} />
                    </div>
                    <div className="info-content">
                      <h4 className="info-title">Website</h4>
                      <a href={coworkingData.website} target="_blank" rel="noopener noreferrer" className="info-link">
                        {coworkingData.website}
                      </a>
                    </div>
                  </div>
                )}

                {(coworkingData?.instagram || coworkingData?.facebook || coworkingData?.linkedin) && (
                  <div className="info-item">
                    <div className="info-icon social-icons">
                      {coworkingData?.instagram && <Instagram size={16} />}
                      {coworkingData?.facebook && <Facebook size={16} />}
                      {coworkingData?.linkedin && <Linkedin size={16} />}
                    </div>
                    <div className="info-content">
                      <h4 className="info-title">Redes Sociais</h4>
                      <div className="social-links">
                        {coworkingData?.instagram && (
                          <a href={coworkingData.instagram} target="_blank" rel="noopener noreferrer">
                            Instagram
                          </a>
                        )}
                        {coworkingData?.facebook && (
                          <a href={coworkingData.facebook} target="_blank" rel="noopener noreferrer">
                            Facebook
                          </a>
                        )}
                        {coworkingData?.linkedin && (
                          <a href={coworkingData.linkedin} target="_blank" rel="noopener noreferrer">
                            LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="coworking-tabs">
        <Tabs>
          {/* <TabsList>
            <TabsTrigger active={activeTab === "users"} onClick={() => setActiveTab("users")}>
              Usuários
            </TabsTrigger>
            <TabsTrigger active={activeTab === "amenities"} onClick={() => setActiveTab("amenities")}>
              Serviços e Comodidades
            </TabsTrigger>
          </TabsList> */}

          {activeTab === "users" && (
            <TabsContent>
              <div className="users-content">
                <UsersList />
              </div>
            </TabsContent>
          )}

          {activeTab === "amenities" && (
            <TabsContent>
              <div className="amenities-content">
                <p>Conteúdo de comodidades</p>
              </div>
            </TabsContent>
          )}

          {activeTab === "plans" && (
            <TabsContent>
              <div className="plans-content">
                <p>Conteúdo de planos</p>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      <CreateUserModal
        isOpen={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
        onSuccess={handleUserCreated}
      />

      <EditCoworkingModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleCoworkingUpdated}
        coworkingData={coworkingData}
      />
    </>
  )
}

export default MeuCoworking
